import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/i18n";
import { Coins, Plus, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["trade_tax", "advertisement_tax", "lease_tax", "fair_tax",
                    "vehicle_tax", "animal_tax", "professional_tax", "other"];

export default function OtherTax() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState({
    category: "trade_tax", payer_name: "", mobile: "", description: "",
    amount: "", payment_mode: "cash", payment_ref: "",
    receipt_date: new Date().toISOString().slice(0, 10),
  });
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = {};
    if (user?.panchayat_id) params.panchayat_id = user.panchayat_id;
    if (filter) params.category = filter;
    api.get("/other-tax", { params }).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [user, filter]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/other-tax", {
        panchayat_id: user?.panchayat_id,
        ...form, amount: parseFloat(form.amount),
      });
      toast.success("Receipt created");
      setShow(false);
      setForm({ ...form, payer_name: "", mobile: "", description: "", amount: "", payment_ref: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <OfficerLayout>
      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">Other Tax</div>
            <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Coins className="w-7 h-7 text-[#0A3161]" />
              {lang === "hi" ? "अन्य कर — रसीद" : "Other Tax Receipts"}
            </h1>
            <p className="mt-1 text-sm text-slate-600">{lang === "hi" ? "ट्रेड, विज्ञापन, पट्टा, मेला, वाहन, पेशा कर" : "Trade, advertisement, lease, fair, vehicle, professional tax."}</p>
          </div>
          <div className="flex gap-2">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-2 rounded-md border border-slate-300 bg-white text-sm" data-testid="ot-filter">
              <option value="">All categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
            </select>
            <button onClick={() => setShow(true)} data-testid="ot-add-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md text-white" style={{ background: "#E87722" }}>
              <Plus className="w-4 h-4" /> {lang === "hi" ? "नई रसीद" : "New Receipt"}
            </button>
          </div>
        </div>

        <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="ot-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              <tr><th className="text-left px-4 py-3">Receipt No.</th><th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Category</th><th className="text-left px-4 py-3">Payer</th>
                  <th className="text-left px-4 py-3">Description</th><th className="text-left px-4 py-3">Mode</th>
                  <th className="text-right px-4 py-3">Amount</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-mono text-xs">{r.receipt_no}</td>
                  <td className="px-4 py-3 text-xs">{r.receipt_date}</td>
                  <td className="px-4 py-3 capitalize text-xs">{r.category.replace(/_/g, " ")}</td>
                  <td className="px-4 py-3"><div className="font-medium">{r.payer_name}</div><div className="text-xs text-slate-500">{r.mobile || ""}</div></td>
                  <td className="px-4 py-3 text-xs max-w-md">{r.description}</td>
                  <td className="px-4 py-3 uppercase text-xs">{r.payment_mode}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{formatINR(r.amount)}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-400">No receipts</td></tr>}
            </tbody>
          </table>
        </div>

        {show && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-md w-full max-w-lg">
              <div className="flex items-center justify-between p-5 border-b border-slate-200">
                <h2 className="font-heading text-xl font-bold">{lang === "hi" ? "नई अन्य-कर रसीद" : "New Other-Tax Receipt"}</h2>
                <button onClick={() => setShow(false)} className="p-2 text-slate-400"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={submit} className="p-5 space-y-3 text-sm">
                <Field label="Category">
                  <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="form-input" data-testid="ot-cat">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
                  </select>
                </Field>
                <Field label="Payer Name">
                  <input required value={form.payer_name} onChange={(e) => setForm({ ...form, payer_name: e.target.value })} className="form-input" data-testid="ot-payer" />
                </Field>
                <Field label="Mobile">
                  <input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="form-input" />
                </Field>
                <Field label="Description">
                  <input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="form-input" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Amount (₹)">
                    <input type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="form-input" data-testid="ot-amount" />
                  </Field>
                  <Field label="Mode">
                    <select value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} className="form-input">
                      {["cash", "upi", "cheque", "card", "online"].map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Receipt Date">
                  <input type="date" required value={form.receipt_date} onChange={(e) => setForm({ ...form, receipt_date: e.target.value })} className="form-input" />
                </Field>
                <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                  <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm rounded-md border border-slate-300">Cancel</button>
                  <button type="submit" disabled={busy} data-testid="ot-submit" className="px-4 py-2 text-sm font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>{busy ? "…" : "Save"}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <style>{`.form-input{width:100%;padding:0.55rem 0.75rem;border-radius:0.375rem;border:1px solid #CBD5E1;background:white;font-size:0.875rem;outline:none}.form-input:focus{border-color:#0A3161;box-shadow:0 0 0 3px rgba(10,49,97,0.08)}`}</style>
    </OfficerLayout>
  );
}

const Field = ({ label, children }) => (
  <div><div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1">{label}</div>{children}</div>
);
